import { XPath } from '../../controls/xpath';
import { Checkbox } from '../../controls/checkbox';
import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomValidationService } from '../../controls/services/custom-validation.service';
import { Layouts } from '../../controls/layouts';
import { Password, PasswordComponentValue } from '../../controls/password';
import { CalculateValidationTextFn } from '../../controls/components/internal/control-supporting-text/control-supporting-text.component';
import { Buttons } from '../../controls/buttons';
import { Datetimebox } from '../../controls/datetimebox';
import { Textbox } from '../../controls/textbox';
import { Selectbox } from '../../controls/selectbox';

@Component({
  selector: 'app-control-test-reactive-forms',
  templateUrl: './control-test-reactive-forms.component.html',
  styleUrl: './control-test-reactive-forms.component.scss',
  imports: [ReactiveFormsModule, JsonPipe, Layouts, Buttons, Datetimebox, Textbox, Selectbox, XPath, Checkbox, Password],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlTestReactiveFormsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  readonly myData: {
    name: string | null;
    email: string | null;
    zip: string | null;
    city: string | null;
    allowShowPassword: boolean | null;
    passwordHash: string | null;
    passwordSecret: PasswordComponentValue;
    xpath: string | null;
    backgroundColor: string | null;
    foregroundColor: string | null;
    moreColors: string[] | null;
    otherColors: string[] | null;
    receiveNewsletter: boolean | null;
    datetime: Date | null;
    date: Date | null;
    time: Date | null;
    whatever: string | null;
  } = {
      name: null,
      email: null,
      zip: null,
      city: null,
      allowShowPassword: null,
      passwordHash: null,
      passwordSecret: { id: '001c1d1fa3624f30b4491b4315cfa383', value: null } as PasswordComponentValue,
      xpath: null,
      backgroundColor: null,
      foregroundColor: null,
      moreColors: null,
      otherColors: null,
      receiveNewsletter: null,
      datetime: null,
      date: null,
      time: null,
      whatever: null,
    };

  readonly allowShowPassword = signal(false);

  readonly colors = signal<Array<{ id: string; name: string; }>>([]);

  readonly reactiveFormGroup: FormGroup<{
    name: FormControl<string | null>;
    email: FormControl<string | null>;
    zip: FormControl<string | null>;
    city: FormControl<string | null>;
    allowShowPassword: FormControl<boolean | null>;
    passwordHash: FormControl<string | null>;
    passwordSecret: FormControl<PasswordComponentValue>;
    xpath: FormControl<string | null>;
    backgroundColor: FormControl<string | null>;
    foregroundColor: FormControl<string | null>;
    moreColors: FormControl<string[] | null>;
    otherColors: FormControl<string[] | null>;
    receiveNewsletter: FormControl<boolean | null>;
    datetime: FormControl<Date | null>;
    date: FormControl<Date | null>;
    time: FormControl<Date | null>;
  }> = this.fb.group({
    name: this.fb.control<string | null>(this.myData.name, { validators: [Validators.pattern('[a-zA-Z ]*'), Validators.required, Validators.maxLength(10)] }),
    email: this.fb.control<string | null>({ value: this.myData.email, disabled: false }, {
      validators: [Validators.email, CustomValidationService.conditionalValidator(() =>
        this.reactiveFormGroup?.controls.receiveNewsletter.value === true, Validators.required)],
    }),
    zip: this.fb.control<string | null>(this.myData.zip),
    city: this.fb.control<string | null>(this.myData.city, { validators: [CustomValidationService.conditionalValidator(() => Boolean(this.reactiveFormGroup?.controls.zip.value), Validators.required)] }),
    allowShowPassword: this.fb.control<boolean | null>(this.myData.allowShowPassword),
    passwordHash: this.fb.control<string | null>(this.myData.passwordHash),
    passwordSecret: this.fb.control<PasswordComponentValue>(this.myData.passwordSecret),
    xpath: this.fb.control<string | null>(this.myData.xpath, { validators: [Validators.required] }),
    backgroundColor: this.fb.control<string | null>({ value: this.myData.backgroundColor, disabled: false }),
    foregroundColor: this.fb.control<string | null>(this.myData.foregroundColor, { validators: [Validators.required] }),
    moreColors: this.fb.control<string[] | null>({ value: this.myData.moreColors, disabled: false }),
    otherColors: this.fb.control<string[] | null>(this.myData.otherColors, { validators: [Validators.required] }),
    receiveNewsletter: this.fb.control<boolean | null>(this.myData.receiveNewsletter),
    datetime: this.fb.control<Date | null>(this.myData.datetime, { validators: [Validators.required] }),
    date: this.fb.control<Date | null>(this.myData.date),
    time: this.fb.control<Date | null>(this.myData.time),
  });

  constructor() {
    // Re-run conditional validators whenever their source fields change.
    this.reactiveFormGroup.controls.receiveNewsletter.valueChanges.subscribe(() => {
      this.reactiveFormGroup.controls.email.updateValueAndValidity({ onlySelf: true });
    });

    this.reactiveFormGroup.controls.zip.valueChanges.subscribe(() => {
      this.reactiveFormGroup.controls.city.updateValueAndValidity({ onlySelf: true });
    });

    this.reactiveFormGroup.controls.allowShowPassword.valueChanges.subscribe((value: boolean | null) => {
      this.allowShowPassword.set(value === true);
    });
  }

  ngOnInit(): void {
    this.refresh();
    this.reactiveFormPatch();
    this.reactiveFormGroup.disable();
  }

  refresh(): void {
    this.colors.set([
      { id: 'ba', name: 'Black' },
      { id: 'bu', name: 'Blue' },
      { id: 'ge', name: 'Green' },
      { id: 'cy', name: 'Cyan' },
      { id: 'wi', name: 'White' },
      { id: 're', name: 'Red' },
      { id: 'ma', name: 'Magenta' },
      { id: 'yl', name: 'Yellow' },
    ]);

    this.myData.name = 'John Doe';
    this.myData.email = 'john@example.com';
    this.myData.zip = '7100';
    this.myData.city = null;
    this.myData.allowShowPassword = false;
    this.myData.passwordHash = null;
    this.myData.passwordSecret = { id: '001c1d1fa3624f30b4491b4315cfa383', value: null } as PasswordComponentValue;
    this.myData.xpath = "/*[local-name()='custom-test-xml']/*[local-name()='whatever-id']";
    this.myData.backgroundColor = null;
    this.myData.foregroundColor = 'red';
    this.myData.moreColors = null;
    this.myData.otherColors = ['bu', 'ge', 're'];
    this.myData.receiveNewsletter = true;
    this.myData.datetime = new Date('2024-05-02T09:42:37Z');
    this.myData.date = new Date(1714600800000); // 2024-05-02T00:00:00+02:00
    this.myData.time = new Date(new Date().setHours(11, 37, 42)); // Today at 11:37:42 local time
    this.myData.whatever = '<init>';
  }

  readonly getValidationText: CalculateValidationTextFn = control =>
    control?.errors?.pattern ? 'Name can only contain letters' : null;

  reactiveFormEdit(): void {
    this.reactiveFormGroup.enable();
  }

  reactiveFormSubmit(): void {
    const formValues = this.reactiveFormGroup.getRawValue();
    this.myData.name = formValues.name;
    this.myData.email = formValues.email;
    this.myData.zip = formValues.zip;
    this.myData.city = formValues.city;
    this.myData.xpath = formValues.xpath;
    this.myData.allowShowPassword = formValues.allowShowPassword;
    this.myData.passwordHash = formValues.passwordHash;
    this.myData.passwordSecret = formValues.passwordSecret;
    this.myData.backgroundColor = formValues.backgroundColor;
    this.myData.foregroundColor = formValues.foregroundColor;
    this.myData.moreColors = formValues.moreColors;
    this.myData.otherColors = formValues.otherColors;
    this.myData.receiveNewsletter = formValues.receiveNewsletter;
    this.myData.datetime = formValues.datetime;
    this.myData.date = formValues.date;
    this.myData.time = formValues.time;
    this.reactiveFormGroup.disable();
  }

  reactiveFormCancel(): void {
    this.reactiveFormPatch();
    this.reactiveFormGroup.markAsUntouched();
    this.reactiveFormGroup.markAsPristine();
    this.reactiveFormGroup.disable();
  }

  reactiveFormClear(): void {
    this.reactiveFormGroup.reset();
    this.reactiveFormGroup.markAsUntouched();
    this.reactiveFormGroup.markAsPristine();
  }

  reactiveFormPatch(): void {
    this.reactiveFormGroup.setValue({
      name: this.myData.name,
      email: this.myData.email,
      zip: this.myData.zip,
      city: this.myData.city,
      xpath: this.myData.xpath,
      allowShowPassword: this.myData.allowShowPassword,
      passwordHash: this.myData.passwordHash,
      passwordSecret: this.myData.passwordSecret,
      backgroundColor: this.myData.backgroundColor,
      foregroundColor: this.myData.foregroundColor,
      moreColors: this.myData.moreColors,
      otherColors: this.myData.otherColors,
      receiveNewsletter: this.myData.receiveNewsletter,
      datetime: this.myData.datetime,
      date: this.myData.date,
      time: this.myData.time,
    });
  }
}
